import ReactApexChart from 'react-apexcharts';
import { Calendar } from "lucide-react";
import styled from 'styled-components';
import React, { useEffect, useMemo, useState } from 'react';
import useDidMountEffect from 'hooks/useDidMountEffect';
import * as Server from 'assets/js/Server';
import DatePicker from 'react-datepicker';
import AdminLayout from '../../layout/AdminLayout';

export default function BestProduct() {
  const [viewType, setViewType] = useState('M');
  const [itemList, setItemList] = useState([]);

  const today = new Date();
  const [startDate, setStartDate] = useState(today);

  // 헬퍼 함수: 무조건 YYYY-MM 문자열만 반환
  const getYearMonth = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getInitialMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [param, setParam] = useState({
    searchMonth: getInitialMonth(),
    salesPersonId: localStorage.getItem('userId'),
    viewType: viewType
  });

  const changeStartDate = date => {
    setStartDate(date);
    setParam(() => ({
      ...param,
      searchMonth: getYearMonth(date), // YYYY-MM
    }));
  };

  const totalSum = useMemo(() => {
    return itemList?.reduce((acc, cur) => acc + Number(cur.totalCount || 0), 0) || 0;
  }, [itemList]);

  const dynamicChartData = useMemo(() => {
    if (!itemList || itemList.length === 0) return { series: [], labels: [] };
    return {
      series: itemList.map(item => Number(item.totalCount || 0)),
      labels: itemList.map(item => item.itemName || 'Unknown')
    };
  }, [itemList]);

  const chartOptions = {
    chart: { type: 'pie', toolbar: { show: false } },
    labels: dynamicChartData.labels,
    colors: ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47'],
    legend: { position: 'bottom', fontSize: '12px' },
    responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: 'bottom' } } }]
  };

  const getItemList = () => {
    Server.sendGet('shopify/sales/item', param, res => {
      setItemList(res?.['resultList'] || []);
    }).then();
  };

  useEffect(() => {
    getItemList();
  }, []);

  useDidMountEffect(() => {
    getItemList();
  }, [
    param.searchMonth,
    param.viewType
  ]);
  useDidMountEffect(() => {
    setParam(prev => ({
      ...prev,
      viewType
    }));
  }, [viewType]);

  return (
    <RecommendWrap>
      <div className='title'>
        <div className='tit-head'>
          <i />
          <h4>Best Item</h4>
        </div>
      </div>
      <div className="container-inner">
        {/* 상단 툴바 */}
        <div className="top-toolbar">
          <div className="select-in">
            <select
              value={viewType}
              onChange={e => setViewType(e.target.value)}
            >
              <option value={'M'}>Monthly</option>
              <option value={'Y'}>Yearly</option>
            </select>
          </div>
          <div className='item-in'>
            <DatePicker
              selected={startDate}
              dateFormat={param.viewType === 'M' ? 'yyyy-MM' : 'yyyy'}
              onChange={date => changeStartDate(date)}
              showMonthYearPicker={param.viewType === 'M'}
              showYearPicker={param.viewType === 'Y'}
            />
          </div>
        </div>

        {/* 좌우 배치 영역 */}
        <div className="main-content">
          <section className="section-card table-box">
            <div className="card-header">Product Sales</div>
            <div className="table-scroll">
              <table>
                <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Product Name</th>
                  <th>Qty</th>
                  <th>Sales</th>
                  <th>%</th>
                </tr>
                </thead>
                <tbody>
                {itemList.map((item, idx) => (
                  <tr key={idx}>
                    <td className="item-name">{item.itemName}</td>
                    <td>{item.totalCount}</td>
                    <td>{Number(item.itemPrice).toLocaleString()}</td>
                    <td>{totalSum > 0 ? ((item.totalCount / totalSum) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section-card chart-box">
            <div className="card-header">Sales Ratio</div>
            <ReactApexChart options={chartOptions} series={dynamicChartData.series} type="pie" height={650} />
          </section>
        </div>
      </div>
    </RecommendWrap>
  );
}

const RecommendWrap = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px;
  min-height: 100%;
  .title {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;

    .tit-head {
      display: flex;
      align-items: center;
      gap: 0 10px;

      i {
        display: block;
        width: 4px;
        height: 32px;
        border-radius: 0 2px 2px 0;
        background: #1e1e1e;
      }

      h4 {
        font-weight: 500;
        font-size: 16px;
        color: #000000;
      }
    }
  }

  * { box-sizing: border-box; } /* 내부 모든 요소 박스모델 고정 */

  .container-inner {
    max-width: 100%; /* 부모 패딩 안에서 꽉 차게 */
    margin: 0 auto;
  }

  .top-toolbar {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 20px;


  .select-in {
    display: flex; 
    align-items: center; 
    gap: 0 20px;
    
    select {
      width: 150px;
      height: 40px;
      padding: 0 10px;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 5px;
      outline: none;
    }
  }

    .item-in {
      display: flex;
      align-items: center;
      gap: 0 10px;

      label {
        display:none;
      }

      input, select {
        width: 160px;
        height: 40px;
        line-height: 40px;
        padding: 0 16px;
        font-weight: 400;
        font-size: 16px;
        color: #777777;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 5px;
        outline: none;
      }

      .react-datepicker__input-container input, .react-datepicker-ignore-onclickoutside {
        background:url('https://ust-erp.s3.ap-northeast-2.amazonaws.com/renewal/icon/calendar.svg') top 50% right 10px no-repeat;
        background-size:18px 18px;
      }
    }
  }

  .main-content {
    display: flex;
    gap: 20px;
    width: 100%;
  }

  .section-card {
    flex: 1;
    min-width: 0; /* Flex 아이템이 삐져나가는 것을 막는 핵심 설정 */
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .card-header {
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 15px;
    color: #1e293b;
  }

  .table-scroll {
    width: 100%;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed; /* 테이블이 영역을 넘지 못하게 고정 */

    th, td {
      padding: 12px 8px;
      font-size: 0.85rem;
      border-bottom: 1px solid #f1f5f9;
      text-align: center;
    }

    th { color: #64748b; background: #f8fafc; }

    .item-name {
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* 기존 버튼 및 스위치 스타일 */
  .view-type-switch {
    display: flex; background: #f1f5f9; padding: 3px; border-radius: 0.75rem;
    button {
      padding: 0.5rem 1rem; border: none; border-radius: 0.6rem; cursor: pointer;
      background: transparent; color: #94a3b8; font-weight: 600;
      &.active { background: #2F80ED; color: #fff; }
    }
  }

  .item-in {
    position:relative;
  }

  @media (max-width: 1024px) {
    .main-content { flex-direction: column; }
  }
`;
